import { CloseIcon } from '@components/Icons/CloseIcon';
import { sendStartOverMessage } from '@utils/station';
import QRCode from 'qrcode';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

interface StationSharePanelProps {
  isOpen: boolean;
  onClose: () => void;
  shareUrl: string;
}

export const StationSharePanel = ({
  isOpen,
  onClose,
  shareUrl,
}: StationSharePanelProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [qrCodeDataUrl, setQrCodeDataUrl] = useState<string>('');

  useEffect(() => {
    if (isOpen && shareUrl) {
      QRCode.toDataURL(shareUrl, {
        width: 200,
        margin: 2,
      })
        .then((url) => {
          setQrCodeDataUrl(url);
        })
        .catch((err) => {
          if (import.meta.env.DEV) {
            console.error('Failed to generate QR code:', err);
          }
        });
    }
  }, [isOpen, shareUrl]);

  const handleStartOver = useCallback(() => {
    // Send start-over message to companion app
    sendStartOverMessage();

    navigate('/');
    onClose();
  }, [navigate, onClose]);

  if (!isOpen) return null;

  return (
    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-4 w-[90vw] max-w-[500px] min-w-[320px] z-[999999]">
      <div className="bg-[rgba(64,64,64,0.8)] backdrop-blur-[12px] md:backdrop-blur-[18px] rounded-[16px] md:rounded-[24px] p-5 md:p-7 flex flex-col items-center relative mx-4">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-white hover:text-white/80 transition-colors duration-200"
          aria-label={t('player.closePanel')}>
          <CloseIcon />
        </button>

        <div className="mb-6">
          <img
            src="/assets/images/logo_napster_white.svg"
            alt="Napster"
            className="h-8 w-auto"
          />
        </div>

        <div className="text-center mb-6">
          <h2 className="font-dm-sans text-[20px] md:text-[28px] font-semibold leading-[24px] md:leading-[32px] tracking-[-0.4px] md:tracking-[-0.28px] text-white">
            {t('player.scanToAccess')}
          </h2>
        </div>

        <div className="mb-6 p-4 bg-white rounded-lg">
          {qrCodeDataUrl ? (
            <img
              src={qrCodeDataUrl}
              alt={t('share.qrCode')}
              className="w-[200px] h-[200px]"
            />
          ) : (
            <div className="w-[200px] h-[200px] flex items-center justify-center">
              <div className="text-gray-400 text-sm">{t('player.loadingQrCode')}</div>
            </div>
          )}
        </div>

        <div className="border-t border-white/20 pt-4 w-full mt-4">
          <button
            onClick={handleStartOver}
            className="w-full flex items-center justify-center gap-2 p-3 bg-white/10 hover:bg-white/20 rounded-lg transition-colors text-white">
            {t('generation.startOver')}
          </button>
        </div>
      </div>
    </div>
  );
};
